import { useEffect, useState, type ReactNode } from 'react';
import { SimpleShell } from '../shell/SimpleShell';
import { getUserEmail, getUserSub, logout } from '../auth';
import { CONFIG } from '../config';
import { runEvent } from '../pageUtils';
import { emitCloseMyPlanningAccount, emitUpdateMyPlanningName, fetchAccountSummary, type AccountSummary } from '../api/account';
import { usePlanningPermissions } from '../usePlanningPermissions';
import { canUseAppLock, isAppLockOn, turnAppLockOff, turnAppLockOn, unlockName, LOCK_AFTER_MS } from '../appLock';

// Only in the installed app, on a device with a built-in unlock. The setting belongs to this device.
function AppLockSetting({ email }: { email: string }) {
  const sub = getUserSub();
  const [available, setAvailable] = useState(false);
  const [on, setOn] = useState(() => isAppLockOn(sub));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = unlockName();
  useEffect(() => {
    let cancelled = false;
    void canUseAppLock().then(ok => { if (!cancelled) setAvailable(ok); });
    return () => { cancelled = true; };
  }, []);
  if (!available || !sub) return null;

  async function turnOn() {
    setBusy(true); setError(null);
    try {
      await turnAppLockOn(sub!, email);
      setOn(true);
    } catch (e) {
      setError(e instanceof DOMException && e.name === 'NotAllowedError' ? 'Cancelled, so the lock is still off.' : `The lock couldn't be turned on: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sx-stack">
      <h2 className="sx-sec-h">App lock</h2>
      <p className="sx-note">
        Ask for {name} when this app opens, and when you come back to it after more than {LOCK_AFTER_MS / 60_000} minute.
        It applies to this device only. Turning it on saves a passkey for Elevations Planning on this device.
      </p>
      {error && <div className="sx-err">{error}</div>}
      {on ? (
        <button type="button" className="sx-cta sx-cta-2" onClick={() => { turnAppLockOff(); setOn(false); }}>Turn off the app lock</button>
      ) : (
        <button type="button" className="sx-cta" disabled={busy} onClick={() => void turnOn()}>{busy ? 'Setting up…' : `Lock with ${name}`}</button>
      )}
    </section>
  );
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
}

const MAX_NAME_LENGTH = 100;

function NameEditor({ summary, onSaved, onCancel }: { summary: AccountSummary; onSaved: (forename: string, surname: string) => void; onCancel: () => void }) {
  const [forename, setForename] = useState(summary.forename ?? '');
  const [surname, setSurname] = useState(summary.surname ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const f = forename.trim();
  const s = surname.trim();
  const unchanged = f === (summary.forename ?? '') && s === (summary.surname ?? '');

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await runEvent(connId => emitUpdateMyPlanningName(f, s, connId));
      onSaved(f, s);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <form className="sx-stack" onSubmit={e => { e.preventDefault(); void save(); }}>
      <label className="sx-field">
        <span className="sx-label">First name</span>
        <input className="sx-input" value={forename} maxLength={MAX_NAME_LENGTH} autoComplete="given-name" onChange={e => setForename(e.target.value)} disabled={saving} />
      </label>
      <label className="sx-field">
        <span className="sx-label">Last name</span>
        <input className="sx-input" value={surname} maxLength={MAX_NAME_LENGTH} autoComplete="family-name" onChange={e => setSurname(e.target.value)} disabled={saving} />
      </label>
      {error && <div className="sx-err">Your name wasn't saved: {error}</div>}
      <button type="submit" className="sx-cta" disabled={!f || !s || unchanged || saving}>{saving ? 'Saving…' : 'Save'}</button>
      <button type="button" className="sx-cta sx-cta-2" onClick={onCancel} disabled={saving}>Cancel</button>
    </form>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return <><dt>{label}</dt><dd>{children}</dd></>;
}

// Closing removes the person's own Planning data and their Planning roles. Their sign-in itself belongs to the
// Foundations platform and may give access to other ELW applications, so it is left alone.
export function AccountPage() {
  const email = getUserEmail() ?? '';
  // Administrators can't close their account (the workflow refuses too), so the app always keeps one.
  const { can, loading: permsLoading } = usePlanningPermissions();
  const isAdmin = can('users:write');
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [summary, setSummary] = useState<AccountSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  useEffect(() => {
    const sub = getUserSub();
    if (!sub) return;
    let cancelled = false;
    fetchAccountSummary(sub)
      .then(d => { if (!cancelled) setSummary(d); })
      .catch((e: Error) => { if (!cancelled) setSummaryError(e.message); });
    return () => { cancelled = true; };
  }, []);
  const skel = <span className="skel" />;
  const name = summary ? [summary.forename, summary.surname].filter(Boolean).join(' ') || '—' : skel;
  const confirmed = email !== '' && typed.trim().toLowerCase() === email.toLowerCase();

  async function close() {
    setBusy(true);
    setError(null);
    try {
      await runEvent(connId => emitCloseMyPlanningAccount(connId), 120_000);
      logout(CONFIG.cognitoDomain, CONFIG.clientId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <SimpleShell>
      <div className="sx-form">
        <div>
          <a className="sx-back" href="#/assess">← Back to my assessments</a>
          <h1 className="sx-h1">Your account</h1>
        </div>

        <section className="sx-stack">
          <h2 className="sx-sec-h">Details</h2>
          {summaryError && <div className="sx-err">Some details couldn't be loaded: {summaryError}</div>}
          {editingName && summary ? (
            <NameEditor
              summary={summary}
              onSaved={(forename, surname) => { setSummary({ ...summary, forename, surname }); setEditingName(false); }}
              onCancel={() => setEditingName(false)}
            />
          ) : null}
          <dl className="sx-facts">
            {!editingName && (
              <Fact label="Name">
                {name}
                {summary && <> <button type="button" className="sx-link" onClick={() => setEditingName(true)}>Edit</button></>}
              </Fact>
            )}
            <Fact label="Email">{email || '—'}</Fact>
            <Fact label="Role">{permsLoading ? skel : isAdmin ? 'Administrator' : 'Member'}</Fact>
            <Fact label="Member since">{summary ? fmtDate(summary.createdAt) : skel}</Fact>
            <Fact label="Terms and privacy">
              {summary
                ? summary.termsAcceptedAt ? `Version ${summary.termsAcceptedVersion}, accepted ${fmtDate(summary.termsAcceptedAt)}` : 'Not yet accepted'
                : skel}
              {' '}<a className="sx-link" href="#/legal">Read</a>
            </Fact>
          </dl>
          <button type="button" className="sx-cta sx-cta-2" onClick={() => logout(CONFIG.cognitoDomain, CONFIG.clientId)}>Sign out</button>
        </section>

        <AppLockSetting email={email} />

        <section className="sx-stack">
          {permsLoading ? <span className="skel" style={{ width: '100%', height: 52 }} /> : isAdmin ? (
            <div className="sx-note">Administrators can't close their own account.</div>
          ) : !confirming ? (
            <button type="button" className="sx-link sx-link-neg" onClick={() => setConfirming(true)}>Close account</button>
          ) : <>
          <h2 className="sx-sec-h">Close your Elevations Planning account</h2>
          <div className="sx-warning">
            <strong>This can't be undone.</strong> When you close your account:
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              <li>Your cases, uploaded documents, assessments and their reports are deleted for good.</li>
              <li>Cases you shared with your whole organisation are kept, so your colleagues still have them.</li>
              <li>Any tokens you have left are lost.</li>
              <li>You lose access to Elevations Planning.</li>
              <li>Your email address and password aren't removed, because they may also sign you in to other ELW Consulting applications.</li>
              <li>Service logs are kept for up to 90 days, as the Privacy Notice explains.</li>
            </ul>
          </div>
          <p className="sx-note">An administrator can invite you again later, but you would start with nothing.</p>
          <label className="sx-field">
            <span className="sx-label">Type your email address to confirm</span>
            <input
              className="sx-input"
              type="email"
              autoComplete="off"
              autoFocus
              value={typed}
              onChange={e => setTyped(e.target.value)}
              disabled={busy}
              placeholder={email}
            />
          </label>
          {error && <div className="sx-err">Your account wasn't closed: {error}</div>}
          <button type="button" className="sx-cta sx-cta-neg" disabled={!confirmed || busy} onClick={() => void close()}>
            {busy ? 'Closing your account…' : 'Close my account'}
          </button>
          <button type="button" className="sx-cta sx-cta-2" disabled={busy} onClick={() => { setConfirming(false); setTyped(''); setError(null); }}>
            Cancel
          </button>
          </>}
        </section>
      </div>
    </SimpleShell>
  );
}
